import { SmsConversationSummary, SmsThreadMessage } from '@numeo/types'

/**
 * The small facts the SMS page and its two halves agree on.
 *
 * Here rather than in either component because both of them need them and neither owns them — the key
 * especially: a conversation is `(line, counterpart)`, and a second definition of that is a second
 * answer to "is this the same thread", which is the bug the key was introduced to fix.
 */

/** The thread key. Both halves, because the same driver on two company numbers is two conversations. */
export const keyOf = (line: string, counterpart: string): string => `${line}|${counterpart}`

/** Initials for the avatar: a name if we have one, otherwise the last two digits of their number. */
export const initialsOf = (name: string | null, counterpart: string): string => {
    const parts = (name ?? '').trim().split(/\s+/).filter(Boolean)
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
    return counterpart.replace(/\D/g, '').slice(-2)
}

/** Day heading between message groups — "Today" / "Yesterday" / the date. */
const dayLabel = (iso: string): string => {
    const date = new Date(iso)
    const today = new Date()
    const yesterday = new Date(today.getTime() - 86_400_000)
    const same = (a: Date, b: Date) => a.toDateString() === b.toDateString()
    if (same(date, today)) return 'Today'
    if (same(date, yesterday)) return 'Yesterday'
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: date.getFullYear() === today.getFullYear() ? undefined : 'numeric' })
}

export const timeOnly = (iso: string): string => new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })

/**
 * Messages under a heading per day, oldest first — what both SMS surfaces render.
 *
 * Here rather than in each: the page and the load drawer had the same nine lines, and a change to how
 * a day is cut would otherwise have to land in two places that look identical and are not checked
 * against each other.
 */
export const groupByDay = (messages: SmsThreadMessage[]): { day: string; items: SmsThreadMessage[] }[] => {
    const out: { day: string; items: SmsThreadMessage[] }[] = []
    for (const message of messages) {
        const day = dayLabel(message.createdAt)
        if (out[out.length - 1]?.day === day) out[out.length - 1].items.push(message)
        else out.push({ day, items: [message] })
    }
    return out
}

/**
 * The inbox's two filters, over the WHOLE loaded list and without a request.
 *
 * Both are client-side on purpose: the page walks every page of conversations before it is done, so a
 * filter here cannot silently miss someone the way a server-side one over a half-loaded list would.
 *
 * `groupFilter` is a group id, {@link UNGROUPED} for the no-desk bucket, or null for all. A conversation
 * with no driver has no desk — a number nobody on the roster answers to — and that is an answer rather than
 * a gap, so it belongs in the no-desk bucket rather than nowhere.
 */
export const filterConversations = (
    conversations: SmsConversationSummary[],
    options: { search: string; groupFilter: string | null; ungrouped: string; groupIdOf: (conversation: SmsConversationSummary) => string | null }
): SmsConversationSummary[] => {
    const term = options.search.trim().toLowerCase()
    const digits = term.replace(/\D/g, '')
    return conversations.filter((c) => {
        if (options.groupFilter !== null) {
            const groupId = options.groupIdOf(c)
            if (options.groupFilter === options.ungrouped ? groupId !== null : groupId !== options.groupFilter) return false
        }
        if (!term) return true
        return (c.name ?? '').toLowerCase().includes(term) || c.counterpart.includes(term) || (!!digits && c.counterpart.includes(digits))
    })
}

/**
 * Which KIND of bucket a desk filter is, for the analytics series — never which desk.
 *
 * A desk's name is the tenant's own vocabulary and has no business in a third-party tool. What the series
 * answers is whether the filter is used at all, and whether the no-desk bucket is the one operators reach
 * for — which would say something about the roster rather than the inbox.
 */
export const deskFilterKind = (groupFilter: string | null, ungrouped: string): 'all' | 'group' | 'ungrouped' =>
    groupFilter === null ? 'all' : groupFilter === ungrouped ? 'ungrouped' : 'group'
